import { navigate } from '../../lib/router';
import { NO_CONNECTIONS_EMPTY_STATE } from '../../lib/definitions/navigation';
import { PROVENANCE_SCOPE_LABEL, type ProvenanceScope } from '../../lib/definitions/trust';
import { UNKNOWN_PROTOCOL_LABEL, type Protocol } from '../../lib/definitions/protocols';

interface LiveMapNoticesProps {
  healthEverLoaded: boolean;
  availableProtocols: Protocol[];
  provenanceScope: ProvenanceScope;
  setProvenanceScope: (scope: ProvenanceScope) => void;
  overflowCount: number;
  unpositionedNodes: readonly unknown[];
  unknownProtocolCount: number;
}

export function LiveMapNotices({ healthEverLoaded, availableProtocols, provenanceScope, setProvenanceScope, overflowCount, unpositionedNodes, unknownProtocolCount }: LiveMapNoticesProps) {
  return (<div className="overflow-note-stack">
        {/* Explains a sparse-looking map rather than leaving it silently
            empty with no reason given — the filter combination this
            control creates (protocol excludes, provenance excludes, region
            dims, node-selection dims) can genuinely show nothing, and
            "why" must always be one glance away, not a guess. */}
        {/* 2026-09-27 first-installer review (part5d, item 6): a fresh
            install's very first thing to notice — promoted from a plain
            .overflow-note line to the same amber "pay attention" banner
            style .radio-caveat-banner/.settings-restart-banner already
            use elsewhere, with a real button to the exact place to fix
            it (Settings → Connections, via #/settings?category=connections
            — router.ts), rather than just naming the path in prose. Same
            NO_CONNECTIONS_EMPTY_STATE registry string as everywhere else
            that shows this hint (NodeListPage/PacketsPage/RadioControlPanel)
            — no new copy, and it only renders while no connection exists.
            2026-09-30 data-layer fix: `availableProtocols` is computed from
            /api/health (AppData.tsx), which reads empty on first render
            before that query has ever resolved — gated on healthEverLoaded
            too so a truly-first-visit/still-loading health check never
            flashes this "no connections configured" banner for a backend
            that in fact has one; it now only appears once health has
            actually answered and genuinely reports zero transports. */}
        {healthEverLoaded && availableProtocols.length === 0 && (
          <div className="no-connections-banner">
            <p>{NO_CONNECTIONS_EMPTY_STATE}</p>
            <button type="button" onClick={() => navigate("#/settings?category=connections")}>
              Go to Settings → Connections
            </button>
          </div>
        )}
        {/* 2026-09-27 first-installer review (part5d, item 4): this used to
            spell out "other observers' traffic is hidden" — a third
            restatement of the same fact the "Filtered:" chip and the
            provenance checkbox already state. Reduced to the shared label
            pair (PROVENANCE_SCOPE_LABEL) plus the one thing this button
            adds that they don't: a one-click way to switch scope. */}
        {provenanceScope === "connected_radio" && (
          <button className="overflow-note" style={{ cursor: "pointer", border: "1px solid var(--border)" }} onClick={() => setProvenanceScope("firehose")}>
            {PROVENANCE_SCOPE_LABEL.connected_radio} (show {PROVENANCE_SCOPE_LABEL.firehose.toLowerCase()} →)
          </button>
        )}
        {overflowCount > 0 && <div className="overflow-note">{overflowCount} animation(s) skipped over cap</div>}
        {unpositionedNodes.length > 0 && (
          // Phase 2, item 2: unpositioned nodes now live properly on the
          // Node List page (filterable to "Unpositioned") rather than in a
          // map-overlay popup list — this just counts and links there.
          <button className="overflow-note" style={{ cursor: "pointer", border: "1px solid var(--border)" }} onClick={() => navigate("#/nodes?f=unpositioned")}>
            {unpositionedNodes.length} node(s) with no known position — not shown on the map (view in Node List →)
          </button>
        )}
        {unknownProtocolCount > 0 && (
          <div className="overflow-note">
            {unknownProtocolCount} node(s) of {UNKNOWN_PROTOCOL_LABEL.toLowerCase()} (grey dot)
          </div>
        )}
      </div>);
}
